import { QuoteFrequency, UnitOfMeasure } from '../types';

/**
 * Safely parses any value (number, string with $, commas, etc.) into a clean finite number.
 */
export function parseNumeric(val: any): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : 0;
  }
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Auto-generates Full Delivery View string from individual address components.
 * Specification Example: "123 Main St | Palm City | FL | 34990"
 */
export function formatFullDeliveryView(
  street?: string,
  city?: string,
  state?: string,
  zipCode?: string
): string {
  const parts = [street, city, state, zipCode]
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p && p.length > 0));

  return parts.join(' | ');
}

/**
 * Generates the standardized Full Quote View string.
 * Specification: [Dollar Amount] + [UOM] + (Vol: [Volume], [Frequency]) + Handling: [Handling Fee] + Fuel: [Fuel %]
 */
export function formatFullQuoteView(
  dollarAmount?: number | string,
  uom?: UnitOfMeasure | string,
  handlingFeeAmount?: number | string,
  noHandlingFee?: boolean,
  fuelSurcharge?: boolean,
  frequency?: QuoteFrequency | string,
  forecastVolume?: number | string,
  fuelSurchargePercent?: number | string,
  noFuelSurcharge?: boolean,
  paymentTerms?: string,
  depositAmount?: number | string | null
): string {
  const price = parseNumeric(dollarAmount);
  const handling = parseNumeric(handlingFeeAmount);
  const volume = Math.max(1, Math.round(parseNumeric(forecastVolume) || 1));

  if (!price && !uom) {
    return '';
  }

  const basePrice = `$${price.toLocaleString()}`;
  const uomStr = uom ? ` / ${uom}` : '';
  const handlingStr = noHandlingFee
    ? 'No Fee'
    : handling
    ? `$${handling.toLocaleString()}`
    : '$0';

  let fuelStr = 'Fuel: No';
  if (!noFuelSurcharge) {
    if (fuelSurchargePercent !== undefined && fuelSurchargePercent !== null && fuelSurchargePercent !== '') {
      const pct = parseNumeric(fuelSurchargePercent);
      fuelStr = pct > 0 ? `Fuel: ${pct}%` : 'Fuel: No';
    } else if (fuelSurcharge) {
      fuelStr = 'Fuel: 15%';
    }
  }

  const volStr = `Vol: ${volume}`;
  const freqDetails = [volStr, frequency].filter(Boolean).join(', ');
  const freqStr = freqDetails ? ` (${freqDetails})` : '';

  let termsStr = '';
  if (paymentTerms && paymentTerms.trim() !== '') {
    const trimmedTerms = paymentTerms.trim();
    if (trimmedTerms === 'Net 7 w/ Deposit') {
      const dep = parseNumeric(depositAmount);
      termsStr = dep > 0
        ? ` + Terms: Net 7 w/ Deposit ($${dep.toFixed(2)})`
        : ` + Terms: Net 7 w/ Deposit`;
    } else {
      termsStr = ` + Terms: ${trimmedTerms}`;
    }
  }

  return `${basePrice}${uomStr}${freqStr} + Handling: ${handlingStr} + ${fuelStr}${termsStr}`;
}

/**
 * Frequency multiplier map estimating monthly shipments.
 */
export const FREQUENCY_MONTHLY_MULTIPLIERS: Record<string, number> = {
  '3x-Weekly': 12,
  '3x Weekly': 12,
  '2x-Weekly': 8,
  '2x Weekly': 8,
  Weekly: 4,
  'Bi-Weekly': 2,
  'Bi Weekly': 2,
  Monthly: 1,
  Once: 1,
  Seasonal: 2,
  TBD: 1,
};

/**
 * Calculates Estimated Deal Value (predicted monthly revenue).
 * Custom formula using Dollar Amount, Forecast Volume, Service Frequency Value, Handling Fee, and Fuel Surcharge %.
 * Formula: [ (Dollar Amount * Forecast Volume * Service Frequency Value) + (Handling Fee * Forecast Volume) ] * (1 + Fuel Surcharge %)
 * Note: Minimum Shipment Amount is for reference only and excluded from this formula.
 */
export function calculateEstimatedDealValue(
  dollarAmount?: number | string,
  uom?: UnitOfMeasure | string,
  handlingFeeAmount?: number | string,
  noHandlingFee?: boolean,
  fuelSurcharge?: boolean,
  frequency?: QuoteFrequency | string,
  minimumMonthlyAmount?: number | string,
  forecastVolume?: number | string,
  fuelSurchargePercent?: number | string,
  noFuelSurcharge?: boolean
): number {
  const price = parseNumeric(dollarAmount);
  const volume = Math.max(1, Math.round(parseNumeric(forecastVolume) || 1));
  const freqVal = frequency ? (FREQUENCY_MONTHLY_MULTIPLIERS[frequency] ?? 1) : 1;
  const handling = noHandlingFee ? 0 : parseNumeric(handlingFeeAmount);

  // Base rate total = Dollar Amount * Forecast Volume * Service Frequency Value
  const baseRateTotal = price * volume * freqVal;

  // Handling total = Handling Fee * Forecast Volume (0 if No-Fee)
  const handlingTotal = handling * volume;

  // Fuel Surcharge multiplier
  let fscMultiplier = 1;
  if (!noFuelSurcharge) {
    if (fuelSurchargePercent !== undefined && fuelSurchargePercent !== null && fuelSurchargePercent !== '') {
      const fscPct = parseNumeric(fuelSurchargePercent);
      fscMultiplier = 1 + fscPct / 100;
    } else if (fuelSurcharge) {
      fscMultiplier = 1.15;
    }
  }

  // Estimated Deal Value (MO) = (baseRateTotal + handlingTotal) * fscMultiplier
  const total = (baseRateTotal + handlingTotal) * fscMultiplier;
  const result = Math.round(total);

  return Number.isFinite(result) ? result : 0;
}

